/**
 * EarningsPage — driver earnings summary by currency + recent ledger.
 * Mirrors Flutter `earnings_page.dart` (driver perspective only for now;
 * sender/receiver/center perspectives land in Phase 4).
 */
import { useTranslation } from 'react-i18next';
import { Loader, DollarSign, CheckCircle, Clock } from 'lucide-react';
import { useEarningsSummary, useEarningsList } from '@/hooks/useEarnings';
import { AppLayout } from '@/components/layout/AppLayout';

export default function EarningsPage() {
  const { t } = useTranslation();
  const summaryQ = useEarningsSummary('driver');
  const listQ = useEarningsList({ status: 'released' });

  return (
    <AppLayout>
      <h1 className="text-xl font-bold text-text-primary mb-4 flex items-center gap-2">
        <DollarSign size={20} /> {t('my_earnings', 'أرباحي')}
      </h1>

      {/* Summary cards */}
      {summaryQ.isLoading ? (
        <div className="flex justify-center py-8 text-text-tertiary">
          <Loader size={28} className="animate-spin" />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
          {(summaryQ.data?.by_currency || []).length === 0 ? (
            <div className="col-span-full bg-surface border border-border rounded-xl p-8 text-center text-text-secondary">
              {t('no_earnings_yet', 'لا أرباح بعد — ابدأ بقبول شحنة')}
            </div>
          ) : (
            (summaryQ.data?.by_currency || []).map((c) => (
              <div key={c.currency} className="bg-surface border border-border rounded-xl p-4 border-t-4 border-t-primary">
                <div className="text-text-tertiary text-xs mb-1">{c.currency}</div>
                <div className="text-2xl font-bold text-primary">{c.total.toLocaleString()}</div>
                <div className="flex justify-between mt-3 text-xs text-text-secondary">
                  <span className="flex items-center gap-1">
                    <CheckCircle size={12} className="text-success" /> {c.released.toLocaleString()}
                  </span>
                  <span className="flex items-center gap-1">
                    <Clock size={12} className="text-warning" /> {c.pending.toLocaleString()}
                  </span>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Recent released */}
      <h2 className="text-lg font-bold text-text-primary mb-3">{t('recent_payouts', 'آخر التحويلات')}</h2>
      {listQ.isLoading ? (
        <div className="flex justify-center py-8 text-text-tertiary">
          <Loader size={28} className="animate-spin" />
        </div>
      ) : !listQ.data || listQ.data.length === 0 ? (
        <div className="bg-surface border border-border rounded-xl p-6 text-center text-text-tertiary">
          {t('no_released_payouts', 'لا تحويلات بعد')}
        </div>
      ) : (
        <div className="bg-surface border border-border rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-background text-text-tertiary text-xs">
              <tr>
                <th className="px-4 py-2 text-start">{t('date', 'التاريخ')}</th>
                <th className="px-4 py-2 text-start">{t('description', 'الوصف')}</th>
                <th className="px-4 py-2 text-end">{t('amount', 'المبلغ')}</th>
              </tr>
            </thead>
            <tbody>
              {listQ.data.map((e) => (
                <tr key={e.id} className="border-t border-border">
                  <td className="px-4 py-2 text-text-secondary">
                    {new Date(e.released_at || e.created_at).toLocaleDateString('ar-SA')}
                  </td>
                  <td className="px-4 py-2 text-text-primary">
                    {e.description || `${t('shipment', 'شحنة')} #${e.shipment_id ?? '—'}`}
                  </td>
                  <td className="px-4 py-2 text-end font-bold text-success">
                    {e.amount.toLocaleString()} {e.currency}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </AppLayout>
  );
}
